// ============================================================
// CANDIDATE PROFILE
// Fix #4: interview date shown + editable (+ optional resend).
// Fix #2: candidate can be deleted (with confirmation).
// Fix #1: step navigation — Move back a step + set any stage as current.
// Fix #7: Paid Trial invitation email with date/time picker.
// Fix #3: cleaner email preview — rendered email + single editor.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Phone, Mail, MapPin, Calendar, Star, CheckCircle2, Clock,
  ChevronDown, X, Trash2, Award, ClipboardCheck, FileCheck, CornerUpLeft, Eye, Pencil, GraduationCap, FileText,
} from 'lucide-react';
import { BACKEND_URL } from '../../App';
import {
  api, STAGES, PIPELINE_STAGES, STAGE_FLOW, PHONE_SCREEN_QUESTIONS,
  ANSWER_TAGS, TAG_COLORS, fmtEventDate, splitEventDate,
  buildEmailPreviewHtml, stripHtml, splitParagraphs, stageLabel,
} from './constants';

export function CandidateProfile({ id, navigate }) {
  const routerNav = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [phoneNotes, setPhoneNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState(false);
  const [expandedStage, setExpandedStage] = useState(null);

  // Unified date/time picker (interview + paid trial)
  const [showPicker, setShowPicker] = useState(false);
  const [pickerType, setPickerType] = useState('interview'); // 'interview' | 'trial'
  const [pickerMode, setPickerMode] = useState('book');       // 'book' | 'edit'
  const [pDate, setPDate] = useState('');
  const [pTime, setPTime] = useState('');

  // Delete + stage-change confirmations
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [stageChangeTo, setStageChangeTo] = useState(null);
  const [changingStage, setChangingStage] = useState(false);

  // Email compose modal
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailMode, setEmailMode] = useState('preview'); // 'preview' | 'edit'
  const [emailEditText, setEmailEditText] = useState('');
  const [emailDirty, setEmailDirty] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const load = useCallback(() => { if (id) api(`/api/recruitment/candidates/${id}`).then(setCandidate).catch(() => {}); }, [id]);
  useEffect(load, [load]);

  // ---- Email helpers (#3) ----
  const showEmail = (data) => {
    setEmailPreview(data);
    setEmailMode('preview');
    setEmailEditText(stripHtml((data.paragraphs || []).join('\n\n')));
    setEmailDirty(false);
  };
  const currentParas = emailPreview ? (emailDirty ? splitParagraphs(emailEditText) : (emailPreview.paragraphs || [])) : [];

  const previewEmail = async (stage, extra = {}) => {    try {
      const data = await api('/api/recruitment/email/preview', { method: 'POST', body: JSON.stringify({ candidate_id: id, stage, ...extra }) });
      if (data.subject) showEmail(data);
    } catch { }
  };

  const sendEmail = async () => {
    if (!emailPreview) return;
    setSendingEmail(true);
    try {
      const paragraphs = emailDirty ? splitParagraphs(emailEditText) : (emailPreview.paragraphs || []);
      const res = await api('/api/recruitment/email/send', { method: 'POST', body: JSON.stringify({ candidate_id: id, to_email: emailPreview.to_email, subject: emailPreview.subject, paragraphs, html_body: emailPreview.html_body }) });
      if (res.success) { toast.success('Email sent'); setEmailPreview(null); }
      else toast.error(res.message || 'Send failed (SMTP)');
    } catch { toast.error('Failed to send'); }
    setSendingEmail(false);
  };

  // ---- Stage movement ----
  const advanceStage = async () => {
    const flow = STAGE_FLOW[candidate.stage];
    if (!flow) return;
    if (flow.next === 'Interview Booked') { openEventPicker('interview', 'book'); return; }
    if (flow.next === 'Paid Trial') { openEventPicker('trial', 'book'); return; }
    const res = await api(`/api/recruitment/candidates/${id}`, { method: 'PATCH', body: JSON.stringify({ stage: flow.next }) });
    toast.success(`Moved to ${stageLabel(flow.next)}`);
    if (flow.next === 'Hired') toast.success('90-day onboarding started in My Training', { duration: 5000 });
    if (flow.email) previewEmail(flow.email);
    load();
  };

  const moveBack = () => {
    const idx = PIPELINE_STAGES.indexOf(candidate.stage);
    if (idx > 0) setStageChangeTo(PIPELINE_STAGES[idx - 1]);
  };

  const confirmStageChange = async () => {
    setChangingStage(true);
    try {
      await api(`/api/recruitment/candidates/${id}`, { method: 'PATCH', body: JSON.stringify({ stage: stageChangeTo }) });
      toast.success(`Moved to ${stageLabel(stageChangeTo)}`);
      if (stageChangeTo === 'Hired') toast.success('90-day onboarding started in My Training', { duration: 5000 });
      setStageChangeTo(null);
      setExpandedStage(null);
      load();
    } catch { toast.error('Failed to move stage'); }
    setChangingStage(false);
  };

  const rejectCandidate = async () => {
    await api(`/api/recruitment/candidates/${id}`, { method: 'PATCH', body: JSON.stringify({ stage: 'Rejected' }) });
    toast.success('Candidate rejected');
    previewEmail('Rejected');
    load();
  };

  const deleteCandidate = async () => {
    setDeleting(true);
    try {
      await api(`/api/recruitment/candidates/${id}`, { method: 'DELETE' });
      toast.success('Candidate deleted');
      navigate('pipeline');
    } catch { toast.error('Failed to delete'); setDeleting(false); }
  };

  const downloadResume = async () => {
    try {
      const token = localStorage.getItem('ps_token');
      const res = await fetch(`${BACKEND_URL}/api/recruitment/candidates/${id}/resume`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not open resume'); }
  };

  // ---- Date/time picker (interview + trial) ----
  const openEventPicker = (type, mode) => {
    const raw = type === 'interview' ? candidate.interview_date : candidate.trial_date;
    const { date, time } = splitEventDate(raw);
    setPDate(date); setPTime(time);
    setPickerType(type); setPickerMode(mode);
    setShowPicker(true);
  };

  const confirmEvent = async (resend) => {
    const iso = pDate + (pTime ? 'T' + pTime : '');
    const dateStr = fmtEventDate(iso);
    const stageName = pickerType === 'interview' ? 'Interview Booked' : 'Paid Trial';
    const body = pickerType === 'interview' ? { interview_date: iso } : { trial_date: iso };
    if (pickerMode === 'book') body.stage = stageName;
    await api(`/api/recruitment/candidates/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    toast.success(pickerMode === 'book' ? `${pickerType === 'interview' ? 'Interview' : 'Paid trial'} scheduled` : 'Date & time updated');
    setShowPicker(false);
    if (pickerMode === 'book' || resend) {
      const extra = pickerType === 'interview' ? { interview_date: dateStr } : { trial_date: dateStr };
      previewEmail(stageName, extra);
    }
    load();
  };

  const tagAnswer = async (question, tag) => {
    const answers = { ...(candidate.application_answers || {}) };
    if (answers[question]) answers[question].tag = tag;
    await api(`/api/recruitment/candidates/${id}`, { method: 'PATCH', body: JSON.stringify({ application_answers: answers }) });
    load();
  };

  const savePhoneNotes = async () => {
    setSavingNotes(true);
    const notes = candidate.notes ? candidate.notes + '\n\n--- Phone Screen Notes ---\n' : 'Phone Screen Notes:\n';
    const allNotes = notes + PHONE_SCREEN_QUESTIONS.map((q, i) => `Q${i + 1}: ${q}\nA: ${phoneNotes[i] || '(no notes)'}`).join('\n\n');
    await api(`/api/recruitment/candidates/${id}`, { method: 'PATCH', body: JSON.stringify({ notes: allNotes }) });
    toast.success('Phone screen notes saved');
    load();
    setSavingNotes(false);
  };

  if (!candidate) return <div className="h-40 bg-muted rounded-2xl animate-skeleton" />;

  const flow = STAGE_FLOW[candidate.stage];
  const stageIdx = STAGES.indexOf(candidate.stage);
  const pipelineIdx = PIPELINE_STAGES.indexOf(candidate.stage);
  const isTerminal = ['Hired', 'Rejected', 'Withdrawn'].includes(candidate.stage);
  const scorecard = candidate.scorecards?.[0];
  const trial = candidate.paid_trials?.[0];
  const ref = candidate.reference_checks?.[0];

  const DatePanel = ({ type }) => {
    const raw = type === 'interview' ? candidate.interview_date : (candidate.trial_date || (trial && trial.trial_date));
    const label = type === 'interview' ? 'Scheduled interview' : 'Scheduled paid trial';
    const tid = type === 'interview' ? 'interview-date-display' : 'trial-date-display';
    const editTid = type === 'interview' ? 'edit-interview-date-btn' : 'edit-trial-date-btn';
    const setTid = type === 'interview' ? 'set-interview-date-btn' : 'set-trial-date-btn';
    return raw ? (
      <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-primary/5" data-testid={tid}>
        <Calendar size={16} className="text-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-sm font-semibold">{fmtEventDate(raw)}</p>
        </div>
        <button onClick={() => openEventPicker(type, 'edit')} data-testid={editTid} className="text-xs text-primary font-medium px-2.5 py-1.5 rounded-lg hover:bg-primary/10 flex-shrink-0">Edit date/time</button>
      </div>
    ) : (
      <div className="flex items-center justify-between mb-4 p-3 rounded-xl bg-muted/30">
        <span className="text-xs text-muted-foreground">No {type === 'interview' ? 'interview' : 'trial'} date set yet</span>
        <button onClick={() => openEventPicker(type, 'edit')} data-testid={setTid} className="text-xs text-primary font-medium">Set date/time</button>
      </div>
    );
  };

  return (
    <div data-testid="candidate-profile">
      <button className="ch-back" onClick={() => navigate('pipeline')}><ArrowLeft size={16} /> Back to Pipeline</button>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-3">
          <span className="text-xs text-muted-foreground font-mono mt-1.5">{candidate.candidate_number}</span>
          <h1 className="text-xl font-bold">{candidate.candidate_name}</h1>
        </div>
        <button onClick={() => setShowDelete(true)} data-testid="delete-candidate-btn"
          className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors">
          <Trash2 size={14} /> Delete
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-6 text-xs text-muted-foreground">
        {candidate.phone && <span className="flex items-center gap-1"><Phone size={12} />{candidate.phone}</span>}
        {candidate.email && <span className="flex items-center gap-1"><Mail size={12} />{candidate.email}</span>}
        <span className="flex items-center gap-1"><MapPin size={12} />{candidate.source}</span>
        {candidate.vacancy_role_title && <span className="flex items-center gap-1"><Award size={12} />{candidate.vacancy_role_title}</span>}
      </div>

      {/* Vertical Stage Tracker — every stage clickable + set-as-current (#1) */}
      <div className="space-y-2 mb-6">
        {PIPELINE_STAGES.map((s, i) => {
          const isCurrent = s === candidate.stage;
          const isPast = stageIdx > i;
          const isOpen = expandedStage === i;

          const stageContent = (() => {
            switch (s) {
              case 'Applied':
              case 'Initial Review':
                return (
                  <div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4 p-3 rounded-xl bg-muted/30" data-testid="application-meta">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Source</p>
                        <p className="text-sm font-semibold" data-testid="application-source">{candidate.source || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Received</p>
                        <p className="text-sm font-semibold" data-testid="application-received">{candidate.created_at ? fmtEventDate(candidate.created_at) : 'Unknown'}</p>
                      </div>
                      {candidate.resume_file_url && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Resume</p>
                          <button onClick={downloadResume} data-testid="download-resume-btn" className="text-sm font-semibold text-primary flex items-center gap-1 hover:underline">
                            <FileText size={13} /> {candidate.resume_filename || 'Download resume'}
                          </button>
                        </div>
                      )}
                    </div>
                    {candidate.application_answers ? (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Application Answers</p>
                        {Object.entries(candidate.application_answers).map(([q, data], qi) => (
                          <div key={qi} className="border-b border-border/30 pb-3 mb-3 last:border-0">
                            <p className="text-xs font-semibold text-muted-foreground mb-1">Q{qi + 1}. {q}</p>
                            <p className="text-sm mb-1 leading-relaxed">{typeof data === 'object' ? data.answer : data}</p>
                            {isCurrent && <div className="flex gap-1 flex-wrap">{ANSWER_TAGS.map(tag => {
                              const tagSelected = (typeof data === 'object' && data.tag === tag);
                              return <button key={tag} onClick={() => tagAnswer(q, tag)} data-testid={`answer-tag-${qi}-${tag.toLowerCase()}`} aria-pressed={tagSelected} className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border transition-colors ${tagSelected ? `${TAG_COLORS[tag]} border-transparent ring-2 ring-offset-1 ring-primary/50` : 'bg-transparent border-border text-muted-foreground hover:bg-muted hover:text-foreground'}`}>{tag}</button>;
                            })}</div>}
                            {typeof data === 'object' && data.tag && !isCurrent && <span className={`text-xs px-2 py-0.5 rounded-full ${TAG_COLORS[data.tag] || 'bg-muted'}`}>{data.tag}</span>}
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-muted-foreground">No application answers recorded</p>}
                  </div>
                );
              case 'Phone Screen':
                return (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Phone Screen Questions</p>
                    {PHONE_SCREEN_QUESTIONS.map((q, qi) => (
                      <div key={qi} className="mb-3">
                        <p className="text-sm font-medium mb-1"><span className="text-primary font-bold">{qi + 1}.</span> {q}</p>
                        {isCurrent && <textarea value={phoneNotes[qi] || ''} onChange={e => setPhoneNotes(n => ({ ...n, [qi]: e.target.value }))} className="rec-input rec-textarea" rows={2} placeholder="Notes..." />}
                      </div>
                    ))}
                    {isCurrent && <button onClick={savePhoneNotes} disabled={savingNotes} className="px-4 py-2 rounded-xl text-xs font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>{savingNotes ? 'Saving...' : 'Save Notes'}</button>}
                  </div>
                );
              case 'Interview Booked':
                return (
                  <div>
                    <DatePanel type="interview" />
                    <p className="text-xs text-muted-foreground mb-3">The 16 interview questions now include inline scoring and per-question notes inside the scorecard, so you can score the applicant as you go.</p>
                    <button onClick={() => navigate('scorecard', id)} className="px-4 py-2 rounded-xl text-xs font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}><Star size={12} className="inline mr-1" /> Open Scorecard</button>
                  </div>
                );
              case 'Interview Completed':
                return scorecard ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-lg font-bold">{scorecard.weighted_total_score}%</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${scorecard.weighted_total_score >= 90 ? 'badge-active' : scorecard.weighted_total_score >= 75 ? 'badge-improving' : 'badge-draft'}`}>{scorecard.score_interpretation}</span>
                    <button onClick={() => navigate('scorecard', id)} className="ml-auto text-xs text-primary font-medium">View Scorecard</button>
                  </div>
                ) : <button onClick={() => navigate('scorecard', id)} className="px-4 py-2 rounded-xl text-xs font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>Fill Scorecard</button>;
              case 'Paid Trial':
                return (
                  <div>
                    <DatePanel type="trial" />
                    {trial ? (
                      <div className="flex items-center gap-3 flex-wrap"><p className="text-sm font-medium">{trial.trial_result || 'In progress'}</p><button onClick={() => navigate('trial', id)} className="ml-auto text-xs text-primary font-medium">Open Trial</button></div>
                    ) : <button onClick={() => navigate('trial', id)} className="px-4 py-2 rounded-xl text-xs font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>Start Trial Observation</button>}
                  </div>
                );
              case 'Reference Check':
                return ref ? (
                  <div><p className="text-sm font-medium">{ref.reference_result || 'Completed'} ({ref.referee_name})</p><button onClick={() => navigate('reference', id)} className="text-xs text-primary font-medium mt-1">View Details</button></div>
                ) : <button onClick={() => navigate('reference', id)} className="px-4 py-2 rounded-xl text-xs font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>Add Reference Check</button>;
              case 'Final Review':
                return <button onClick={() => navigate('decision', id)} className="px-4 py-2 rounded-xl text-xs font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>Open Decision Dossier</button>;
              case 'Offer Made':
                return <p className="text-xs text-muted-foreground">Offer email sent. Waiting for candidate response.</p>;
              default:
                return null;
            }
          })();

          const showSetCurrent = !isTerminal && !isCurrent;

          return (
            <div key={s} className="ps-card-static overflow-hidden">
              <button onClick={() => setExpandedStage(isOpen ? null : i)} className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/20 ${isCurrent ? 'bg-primary/5' : ''}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${isPast ? 'bg-[hsl(var(--success))] text-white' : isCurrent ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                  {isPast ? <CheckCircle2 size={14} /> : i + 1}
                </div>
                <span className={`text-sm flex-1 ${isCurrent ? 'font-semibold' : isPast ? '' : 'text-muted-foreground/60'}`}>{stageLabel(s)}</span>
                {isCurrent && <span className="text-xs px-2 py-0.5 rounded-full bg-primary text-white mr-2">Current</span>}
                <ChevronDown size={14} className={`text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 border-t border-border/50 pt-3">
                  {showSetCurrent && (
                    <button onClick={() => setStageChangeTo(s)} data-testid={`set-current-${s.replace(/\s+/g, '-').toLowerCase()}`}
                      className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-input hover:bg-muted transition-colors">
                      <CornerUpLeft size={13} /> Set as current stage
                    </button>
                  )}
                  {stageContent}
                </div>
              )}
            </div>
          );
        })}
        {isTerminal && (
          <div className={`ps-card-static p-4 flex items-center gap-3 ${candidate.stage === 'Hired' ? 'bg-[hsl(142_40%_95%)]' : 'bg-[hsl(0_80%_97%)]'}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${candidate.stage === 'Hired' ? 'bg-[hsl(var(--success))] text-white' : 'bg-destructive text-white'}`}>
              {candidate.stage === 'Hired' ? <CheckCircle2 size={14} /> : <X size={14} />}
            </div>
            <span className="text-sm font-semibold flex-1">{stageLabel(candidate.stage)}</span>
            <button onClick={() => setStageChangeTo('Applied')} className="text-xs font-medium px-3 py-1.5 rounded-lg border border-input hover:bg-muted transition-colors">Reopen</button>
          </div>
        )}
        {candidate.stage === 'Hired' && candidate.training_profile_id && (
          <div className="ps-card-static p-4 flex items-center gap-3" data-testid="onboarding-panel">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0"><GraduationCap size={18} className="text-primary" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Onboarding started</p>
              <p className="text-xs text-muted-foreground">A 90-day training &amp; trial record was created in My Training{candidate.onboarding_started_at ? ` on ${fmtEventDate(candidate.onboarding_started_at)}` : ''}.</p>
            </div>
            <button onClick={() => routerNav(`/my-training?profile=${candidate.training_profile_id}`)} data-testid="open-onboarding-btn" className="text-xs font-semibold px-3 py-2 rounded-lg flex-shrink-0" style={{ background: 'hsl(var(--primary))', color: 'white' }}>Open onboarding</button>
          </div>
        )}
      </div>

      {/* Action Buttons — advance / move back / reject (#1) */}
      {!isTerminal && (
        <div className="flex flex-wrap gap-3 mb-6">
          {flow && (
            <button onClick={advanceStage} data-testid="advance-stage-btn" className="flex-1 min-w-[180px] flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--success))', color: 'white' }}>
              <CheckCircle2 size={16} /> {flow.nextLabel}
            </button>
          )}
          <button onClick={moveBack} disabled={pipelineIdx <= 0} data-testid="move-back-btn" className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold border border-input hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <CornerUpLeft size={16} /> Move back a step
          </button>
          <button onClick={rejectCandidate} className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--destructive))', color: 'white' }}>
            <X size={16} /> Reject
          </button>
        </div>
      )}

      {/* Quick access cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <button onClick={() => navigate('scorecard', id)} className="ps-card p-3 text-left text-xs">
          <Star size={16} className="text-primary mb-1" />
          <p className="font-semibold">Scorecard</p>
          <p className="text-muted-foreground mt-0.5">{scorecard ? `${scorecard.weighted_total_score}%` : 'Not done'}</p>
        </button>
        <button onClick={() => navigate('trial', id)} className="ps-card p-3 text-left text-xs">
          <ClipboardCheck size={16} className="text-primary mb-1" />
          <p className="font-semibold">Trial</p>
          <p className="text-muted-foreground mt-0.5">{trial ? (trial.trial_result || 'Done') : 'Not done'}</p>
        </button>
        <button onClick={() => navigate('reference', id)} className="ps-card p-3 text-left text-xs">
          <FileCheck size={16} className="text-primary mb-1" />
          <p className="font-semibold">Reference</p>
          <p className="text-muted-foreground mt-0.5">{ref ? (ref.reference_result || 'Done') : 'Not done'}</p>
        </button>
        <button onClick={() => navigate('decision', id)} className="ps-card p-3 text-left text-xs">
          <Award size={16} className="text-primary mb-1" />
          <p className="font-semibold">Decision</p>
          <p className="text-muted-foreground mt-0.5">{candidate.hiring_decision ? (candidate.hiring_decision.decision || 'In progress') : 'Not started'}</p>
        </button>
      </div>

      {/* Notes */}
      {candidate.notes && (
        <div className="ps-card-static p-5 mb-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Notes</h3>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{candidate.notes}</p>
        </div>
      )}

      {/* Timeline */}
      {candidate.stage_history && candidate.stage_history.length > 0 && (
        <details className="ps-card-static overflow-hidden">
          <summary className="p-5 cursor-pointer flex items-center gap-2">
            <Clock size={14} className="text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Timeline ({candidate.stage_history.length} events)</span>
          </summary>
          <div className="px-5 pb-5 space-y-2">
            {candidate.stage_history.map((h, i) => (
              <div key={i} className="flex items-center gap-3 text-xs">
                <Clock size={12} className="text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">{h.timestamp ? new Date(h.timestamp).toLocaleString() : ''}</span>
                <span className="font-medium">{h.stage}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Date/Time Picker Modal (interview + trial) */}
      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-card rounded-2xl shadow-[var(--shadow-panel)] max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold">{pickerMode === 'book' ? `Schedule ${pickerType === 'interview' ? 'Interview' : 'Paid Trial'}` : `Edit ${pickerType === 'interview' ? 'Interview' : 'Paid Trial'} Date & Time`}</h3>
              <button onClick={() => setShowPicker(false)} className="p-2 rounded-lg hover:bg-muted"><X size={18} /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">{pickerMode === 'book' ? `Set the date and time. It will be included in the invitation email sent to the candidate.` : `Update the date and time. You can optionally resend the updated invitation.`}</p>
            <div className="space-y-3">
              <div className="rec-field">
                <label className="rec-label">Date</label>
                <input type="date" value={pDate} onChange={e => setPDate(e.target.value)} className="rec-input" data-testid="event-date-input" />
              </div>
              <div className="rec-field">
                <label className="rec-label">Time</label>
                <input type="time" value={pTime} onChange={e => setPTime(e.target.value)} className="rec-input" data-testid="event-time-input" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              {pickerMode === 'book' ? (
                <button onClick={() => confirmEvent(true)} disabled={!pDate} data-testid="book-event-btn" className="flex-1 px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50" style={{ background: 'hsl(var(--primary))', color: 'white' }}>
                  <Calendar size={14} className="inline mr-2" />Schedule & Send Email
                </button>
              ) : (
                <>
                  <button onClick={() => confirmEvent(false)} disabled={!pDate} data-testid="save-event-btn" className="flex-1 px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50" style={{ background: 'hsl(var(--success))', color: 'white' }}>Save Time</button>
                  <button onClick={() => confirmEvent(true)} disabled={!pDate} className="flex-1 px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50" style={{ background: 'hsl(var(--primary))', color: 'white' }}><Mail size={14} className="inline mr-2" />Save & Resend</button>
                </>
              )}
              <button onClick={() => setShowPicker(false)} className="px-5 py-2.5 rounded-xl text-sm font-medium border border-input">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Stage-change confirmation (#1) */}
      {stageChangeTo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-card rounded-2xl shadow-[var(--shadow-panel)] max-w-sm w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><CornerUpLeft size={18} className="text-primary" /></div>
              <h3 className="text-sm font-bold">Move to "{stageLabel(stageChangeTo)}"?</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-5">This changes the candidate's current stage. No email will be sent automatically for this move.</p>
            <div className="flex gap-3">
              <button onClick={confirmStageChange} disabled={changingStage} data-testid="confirm-stage-change-btn" className="flex-1 px-5 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>{changingStage ? 'Moving...' : 'Move'}</button>
              <button onClick={() => setStageChangeTo(null)} className="px-5 py-2.5 rounded-xl text-sm font-medium border border-input">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-card rounded-2xl shadow-[var(--shadow-panel)] max-w-sm w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0"><Trash2 size={18} className="text-destructive" /></div>
              <h3 className="text-sm font-bold">Delete this candidate?</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-5">This permanently removes <strong>{candidate.candidate_name}</strong> and all their scorecards, trials, references and decision records. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={deleteCandidate} disabled={deleting} data-testid="confirm-delete-btn" className="flex-1 px-5 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--destructive))', color: 'white' }}>{deleting ? 'Deleting...' : 'Delete'}</button>
              <button onClick={() => setShowDelete(false)} className="px-5 py-2.5 rounded-xl text-sm font-medium border border-input">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Email Compose Modal — rendered preview + single editor (#3) */}
      {emailPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-card rounded-2xl shadow-[var(--shadow-panel)] max-w-2xl w-full max-h-[88vh] overflow-y-auto mx-4" data-testid="email-modal">
            <div className="p-5 border-b border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold">Review & Send Email</h3>
                <button onClick={() => setEmailPreview(null)} className="p-2 rounded-lg hover:bg-muted"><X size={18} /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="rec-field">
                  <label className="rec-label">To</label>
                  <input value={emailPreview.to_email || ''} onChange={e => setEmailPreview(p => ({ ...p, to_email: e.target.value }))} className="rec-input" data-testid="email-to-input" />
                </div>
                <div className="rec-field">
                  <label className="rec-label">Subject</label>
                  <input value={emailPreview.subject || ''} onChange={e => setEmailPreview(p => ({ ...p, subject: e.target.value }))} className="rec-input" data-testid="email-subject-input" />
                </div>
              </div>
              {/* Preview / Edit toggle */}
              <div className="flex gap-1 p-1 rounded-xl bg-muted mt-4 w-fit">
                <button onClick={() => setEmailMode('preview')} data-testid="email-preview-tab" className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium ${emailMode === 'preview' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}><Eye size={13} /> Preview</button>
                <button onClick={() => setEmailMode('edit')} data-testid="email-edit-tab" className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium ${emailMode === 'edit' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}><Pencil size={13} /> Edit text</button>
              </div>
            </div>
            <div className="p-5">
              {emailMode === 'preview' ? (
                <div className="rounded-xl overflow-hidden border border-border">
                  <iframe title="Email preview" data-testid="email-preview-frame" srcDoc={buildEmailPreviewHtml(currentParas)} style={{ width: '100%', height: '440px', border: 'none', background: '#f4f2ef' }} />
                </div>
              ) : (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Edit the message below. Leave a blank line between paragraphs. The greeting and sign-off are added automatically.</p>
                  <textarea value={emailEditText} onChange={e => { setEmailEditText(e.target.value); setEmailDirty(true); }} data-testid="email-edit-textarea" className="rec-input" style={{ minHeight: '320px', lineHeight: 1.7 }} />
                </div>
              )}
            </div>
            <div className="p-4 border-t border-border flex gap-3">
              <button onClick={sendEmail} disabled={sendingEmail || !emailPreview.to_email} data-testid="email-send-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50" style={{ background: 'hsl(var(--primary))', color: 'white' }}>
                <Mail size={16} /> {sendingEmail ? 'Sending...' : 'Send Email'}
              </button>
              <button onClick={() => setEmailPreview(null)} className="px-5 py-2.5 rounded-xl text-sm font-medium border border-input">Skip</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
